import { groupArray } from '~/utilities/arrayUtils'
import { compareDay } from '~/utilities/dateOperations'

import type { Action } from '../common/actions'
import {
    FetchedDefaultJob,
    FetchedLastSerialOfDefaultJob,
    FileRangeFetchFailed,
    FileRangeFetchingStarted,
    FileRangeWasFetched,
} from '../job/actions'
import {
    FilesWereDeselected,
    FilesWereSelected,
    SelectionActionConfirmHandled,
} from '../selectedFiles/actions'

import {
    SectionsWereExpired,
    TimelineFileSelectingStarted,
    TimelineFilterSet,
    TimelineMonthsFetched,
    TimelineSectionsFetched,
} from './actions'
import { TimelineSectionReference__asDays, TimelineSectionReference__asString } from './index'

export enum MonthFetchStatus {
    UNFETCHED = 'UNFETCHED',
    FETCHING = 'FETCHING',
    FETCHED = 'FETCHED',
}
export type TimelineMonthCountDetail = {
    pictures: number
    videos: number
    screenshots: number
}
export type TimelineMonthSection = {
    year: number
    month: number
    startDay: number // 1-31 (end inclusive)
    endDay: number // 1-31 (end inclusive)
    counts: TimelineMonthCountDetail
    fetchStatus: MonthFetchStatus
}

export type MediaFilterMode = 'all' | 'only_images' | 'only_videos'
export type RecentsFilterMode = 'recents_1' | 'recents_7' | 'recents_30'

export type TimelineFilterMode = MediaFilterMode | RecentsFilterMode

export type TimelineState = {
    timelineJob?: JobID
    initialLastChange: number
    sections?: TimelineMonthSection[]
    filter: TimelineFilterMode
    isSelectMode: boolean
}

const initialState: TimelineState = {
    initialLastChange: -1,
    filter: 'all',
    isSelectMode: false,
}

const updateStateWithFileRangeAction = (
    state: TimelineState,
    start: DayRef,
    end: DayRef,
    newStatus: MonthFetchStatus,
): TimelineState => {
    if (state.sections) {
        return {
            ...state,
            sections: state.sections.map((m) => {
                const mDays = TimelineSectionReference__asDays(m)
                if (compareDay(start, mDays.start) >= 0 && compareDay(end, mDays.end) <= 0) {
                    return { ...m, fetchStatus: newStatus }
                }
                return m
            }),
        }
    }

    return state
}

export function timelineReducer(
    state: TimelineState = initialState,
    action: Action,
): TimelineState {
    if (FetchedDefaultJob.match(action)) {
        return { ...state, timelineJob: action.payload.id }
    }

    if (FetchedLastSerialOfDefaultJob.match(action)) {
        return { ...state, initialLastChange: action.payload }
    }

    if (TimelineMonthsFetched.match(action)) {
        return {
            ...state,
            sections: action.payload.months.map(
                ({ year, month, picture_count, screenshot_count, video_count }) => {
                    const section: TimelineMonthSection = {
                        year,
                        month,
                        startDay: 1,
                        endDay: 31,
                        counts: {
                            pictures: picture_count,
                            videos: video_count,
                            screenshots: screenshot_count,
                        },
                        fetchStatus: MonthFetchStatus.UNFETCHED,
                    }
                    return section
                },
            ),
        }
    }
    if (TimelineSectionsFetched.match(action)) {
        return {
            ...state,
            sections: action.payload.sections.map(
                ({ year, month, startDay, endDay, ...s }): TimelineMonthSection => ({
                    year,
                    month,
                    startDay,
                    endDay,
                    counts: {
                        pictures: s.pictureCount,
                        videos: s.videoCount,
                        screenshots: s.screenshotCount,
                    },
                    fetchStatus: MonthFetchStatus.UNFETCHED,
                }),
            ),
        }
    }

    if (TimelineFilterSet.match(action)) {
        return { ...state, filter: action.payload }
    }

    if (TimelineFileSelectingStarted.match(action)) {
        return { ...state, isSelectMode: true }
    }
    if (
        (FilesWereSelected.match(action) || FilesWereDeselected.match(action)) &&
        state.isSelectMode
    ) {
        return { ...state, isSelectMode: false }
    }

    if (
        FileRangeFetchingStarted.match(action) ||
        FileRangeWasFetched.match(action) ||
        FileRangeFetchFailed.match(action)
    ) {
        const newStatus = {
            [FileRangeFetchingStarted.type]: MonthFetchStatus.FETCHING,
            [FileRangeWasFetched.type]: MonthFetchStatus.FETCHED,
            [FileRangeFetchFailed.type]: MonthFetchStatus.UNFETCHED,
        }[action.type]
        return updateStateWithFileRangeAction(
            state,
            action.payload.start,
            action.payload.end,
            newStatus,
        )
    }
    if (SelectionActionConfirmHandled.match(action)) {
        return { ...state, isSelectMode: false }
    }

    if (SectionsWereExpired.match(action)) {
        const lookup = groupArray(
            action.payload.expiredSections,
            TimelineSectionReference__asString,
        )
        const sections = state.sections?.map((s) => {
            const match = lookup[TimelineSectionReference__asString(s)]
            return match !== undefined
                ? {
                      ...s,
                      counts: match[0].counts,
                      fetchStatus: MonthFetchStatus.UNFETCHED,
                  }
                : s
        })

        return { ...state, sections }
    }

    return state
}

export const timelineReducerMapObj = {
    timeline: timelineReducer,
}
export type StateWithTimeline = StateOfReducerMapObj<typeof timelineReducerMapObj>
